import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';
import { cn } from '@shared/lib/utils';

interface PaginationProps {
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  className?: string;
  /** How many page buttons to show on each side of the current page */
  siblingCount?: number;
}

type PageItem = number | 'ellipsis-left' | 'ellipsis-right';

const getPageItems = (page: number, totalPages: number, siblingCount: number): PageItem[] => {
  const totalNumbers = siblingCount * 2 + 5;

  if (totalPages <= totalNumbers) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(page - siblingCount, 1);
  const rightSibling = Math.min(page + siblingCount, totalPages);
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < totalPages - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftCount = 3 + siblingCount * 2;
    const leftRange = Array.from({ length: leftCount }, (_, i) => i + 1);
    return [...leftRange, 'ellipsis-right', totalPages];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightCount = 3 + siblingCount * 2;
    const rightRange = Array.from(
      { length: rightCount },
      (_, i) => totalPages - rightCount + 1 + i
    );
    return [1, 'ellipsis-left', ...rightRange];
  }

  const middleRange = Array.from(
    { length: rightSibling - leftSibling + 1 },
    (_, i) => leftSibling + i
  );
  return [1, 'ellipsis-left', ...middleRange, 'ellipsis-right', totalPages];
};

export function Pagination({
  page,
  limit,
  total,
  onPageChange,
  className,
  siblingCount = 1,
}: PaginationProps) {
  const { t } = useTranslation('common');
  const totalPages = Math.max(1, Math.ceil(total / Math.max(limit, 1)) || 1);
  const currentPage = Math.min(Math.max(page, 1), totalPages);
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  if (total <= 0 || totalPages <= 1) {
    return null;
  }

  const from = (currentPage - 1) * limit + 1;
  const to = Math.min(currentPage * limit, total);
  const items = getPageItems(currentPage, totalPages, siblingCount);

  return (
    <div
      className={cn(
        'mt-3 flex flex-col gap-3 rounded-2xl border border-border bg-card px-3 py-3 shadow-sm',
        'sm:flex-row sm:items-center sm:justify-between sm:px-4',
        className
      )}
    >
      <p className="text-sm text-muted-foreground">
        {t('common.pagination.showing', { from, to, total })}
      </p>

      <nav
        role="navigation"
        aria-label={t('common.pagination.navLabel')}
        className="flex items-center justify-center"
      >
        <ul className="flex flex-wrap items-center gap-1">
          <li>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-tap w-tap rounded-lg sm:h-8 sm:w-8"
              disabled={!canGoPrev}
              onClick={() => onPageChange(currentPage - 1)}
              aria-label={t('common.pagination.previous')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </li>

          {items.map((item) => {
            if (item === 'ellipsis-left' || item === 'ellipsis-right') {
              return (
                <li key={item} className="flex h-tap w-tap items-center justify-center sm:h-8 sm:w-8">
                  <MoreHorizontal className="h-4 w-4 text-muted-foreground" aria-hidden />
                  <span className="sr-only">{t('common.pagination.ellipsis')}</span>
                </li>
              );
            }

            const isActive = item === currentPage;
            return (
              <li key={item}>
                <Button
                  type="button"
                  variant={isActive ? 'default' : 'outline'}
                  size="icon"
                  className={cn(
                    'h-tap w-tap rounded-lg text-sm tabular-nums sm:h-8 sm:w-8',
                    isActive && 'pointer-events-none'
                  )}
                  onClick={() => onPageChange(item)}
                  aria-label={t('common.pagination.page', { page: item })}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {item}
                </Button>
              </li>
            );
          })}

          <li>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-tap w-tap rounded-lg sm:h-8 sm:w-8"
              disabled={!canGoNext}
              onClick={() => onPageChange(currentPage + 1)}
              aria-label={t('common.pagination.next')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </li>
        </ul>
      </nav>
    </div>
  );
}
